import { getAccessToken, logout } from './auth';

export interface LanguageRef {
  id:          number;
  description: string;
}

export interface Servicepartner {
  id:                       number;
  description:              string;
  public_name:             string | null;
  adr:                      string;
  plz:                      string;
  town:                     string;
  mail:                     string;
  country_code:             string;
  service_contact:          string | null;
  preferred_language:       LanguageRef | null;
  wants_customer_invoices:  boolean | null;
  /** Interner Servicepartner: Lieferschein und Rücklieferschein werden sofort abgeschlossen. */
  instant_slip_closing:     boolean;
}

export interface ServicepartnerPayload {
  description:              string;
  public_name:             string | null;
  adr:                      string;
  plz:                      string;
  town:                     string;
  mail:                     string;
  country_code:             string;
  service_contact:          string | null;
  preferred_language_id:    number;
  wants_customer_invoices:  boolean;
  instant_slip_closing:     boolean;
}

export type ServicepartnerUpdatePayload = Partial<ServicepartnerPayload>;

export interface ServicepartnerAccount {
  id:         number;
  username:   string;
  mail:       string | null;
  is_active:  boolean;
  last_login: string | null;
}

/**
 * Eine Bezeichnung wird bewusst NICHT mitgeschickt: der Zugang wird über seinen
 * Benutzernamen wiedererkannt, und ein zweiter Name dafür war eine Angabe, die
 * sich beim Anlegen jemand ausdenken musste. Der Endpoint nimmt das Feld weiter
 * an (bestehende Zugänge tragen ihre alte Bezeichnung unverändert weiter).
 */
export interface ServicepartnerAccountPayload {
  username: string;
  password: string;
  mail:     string | null;
}

export interface ServicepartnerAccountUpdatePayload {
  mail?:      string | null;
  is_active?: boolean;
}

export interface Country {
  code:        string;
  description: string;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/servicepartner`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  // 401 → Token abgelaufen / widerrufen. Lokal aufräumen, Caller meldet's.
  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

export async function list(): Promise<Servicepartner[]> {
  const body = await request<Servicepartner>(BASE, { method: 'GET' });
  return body.items ?? [];
}

export async function create(payload: ServicepartnerPayload): Promise<Servicepartner> {
  const body = await request<Servicepartner>(BASE, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function update(id: number, payload: ServicepartnerUpdatePayload): Promise<Servicepartner> {
  const body = await request<Servicepartner>(`${BASE}/${id}`, {
    method: 'PATCH',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function disable(id: number): Promise<void> {
  await request<Servicepartner>(`${BASE}/${id}`, { method: 'DELETE' });
}

/**
 * Portal-Zugänge eines Servicepartners (HandyShop Partnerportal).
 * Ein Klartext-Passwort geht nur hinaus, nie herein — der Server gibt es nie zurück.
 */
export async function listAccounts(servicepartnerId: number): Promise<ServicepartnerAccount[]> {
  const body = await request<ServicepartnerAccount>(`${BASE}/${servicepartnerId}/accounts`, { method: 'GET' });
  return body.items ?? [];
}

export async function createAccount(
  servicepartnerId: number,
  payload: ServicepartnerAccountPayload,
): Promise<ServicepartnerAccount> {
  const body = await request<ServicepartnerAccount>(`${BASE}/${servicepartnerId}/accounts`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function updateAccount(
  servicepartnerId: number,
  userId: number,
  payload: ServicepartnerAccountUpdatePayload,
): Promise<ServicepartnerAccount> {
  const body = await request<ServicepartnerAccount>(`${BASE}/${servicepartnerId}/accounts/${userId}`, {
    method: 'PATCH',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function setAccountPassword(
  servicepartnerId: number,
  userId: number,
  password: string,
): Promise<ServicepartnerAccount> {
  const body = await request<ServicepartnerAccount>(`${BASE}/${servicepartnerId}/accounts/${userId}/password`, {
    method: 'POST',
    body:   JSON.stringify({ password }),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Deaktiviert den Zugang — löscht ihn nicht. */
export async function disableAccount(servicepartnerId: number, userId: number): Promise<ServicepartnerAccount> {
  const body = await request<ServicepartnerAccount>(`${BASE}/${servicepartnerId}/accounts/${userId}`, {
    method: 'DELETE',
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export interface ImpersonationLink {
  url:        string;
  expires_in: number;
}

/**
 * Einmal-Link, mit dem sich der Aufrufer am Partnerportal als dieser Zugang
 * anmeldet. Gilt einmalig und kurz; der Mitarbeiter wird serverseitig aus dem
 * Token genommen und protokolliert.
 */
export async function createImpersonationLink(
  servicepartnerId: number,
  userId: number,
): Promise<ImpersonationLink> {
  const body = await request<never>(
    `${BASE}/${servicepartnerId}/accounts/${userId}/impersonation-link`,
    { method: 'POST' },
  );
  const envelope = body as unknown as { url?: string; expires_in?: number };
  if (!envelope.url) {
    throw new ApiError('Server hat keinen Link zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return { url: envelope.url, expires_in: envelope.expires_in ?? 300 };
}

export async function listCountries(): Promise<Country[]> {
  const body = await request<Country>(`${window.location.origin}/api/countries`, { method: 'GET' });
  return body.items ?? [];
}

export async function listLanguages(): Promise<LanguageRef[]> {
  const body = await request<LanguageRef>(`${window.location.origin}/api/languages`, { method: 'GET' });
  return body.items ?? [];
}
